import React, { useState } from 'react';
import TargetTitle from '../../activation-code/components/TargetTitle';
import { getBatchStatus, getUsageRate } from '../../activation-code/utils/batchStatus';
import { useByBatch, useSummaryOptions } from '../hooks/useActivationReport';
import { useReportList } from '../hooks/useReportList';
import { activationReportService } from '../services/activationReportService';
import { BatchRow, ReportFilters } from '../types';
import { batchWorkbook, exportFileName, exportList } from '../utils/exportReport';
import { formatYmd } from '../utils/format';
import ExportButton from './ExportButton';
import Pagination from './Pagination';
import ReportState from './ReportState';
import UsedCodesDialog from './UsedCodesDialog';

interface ByBatchTabProps {
  filters: ReportFilters;
  onChange: (patch: Partial<ReportFilters>) => void;
}

const TH = 'px-4 py-3 text-xs font-medium text-gray-500 uppercase';
const num = (n: number) => n.toLocaleString('vi-VN');

const ByBatchTab: React.FC<ByBatchTabProps> = ({ filters, onChange }) => {
  const setPage = (p: number) => onChange({ page: p });
  const { data, page, perPage, stateProps } = useReportList(useByBatch(filters), filters, setPage);
  const [selected, setSelected] = useState<BatchRow | null>(null);
  const summaryOptions = useSummaryOptions(filters.school_id);

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <ExportButton
          onExport={() =>
            exportList(activationReportService.byBatch, filters, (items) => batchWorkbook(items, filters, summaryOptions))
          }
          fileName={exportFileName('theo-lo', filters)}
        />
      </div>

      <ReportState {...stateProps}>
        {data && (
          <div>
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className={`${TH} text-left`}>Lô</th>
                      <th className={`${TH} text-left`}>Đích</th>
                      <th className={`${TH} text-right`}>Tổng</th>
                      <th className={`${TH} text-right`}>Đã dùng</th>
                      <th className={`${TH} text-right`}>Còn lại</th>
                      <th className={`${TH} text-right`}>Đã thu hồi</th>
                      <th className={`${TH} text-right`}>Hết hạn</th>
                      <th className={`${TH} text-left`}>Tỉ lệ dùng</th>
                      <th className={`${TH} text-left`}>Trạng thái</th>
                      <th className={`${TH} text-right`}>Dùng trong kỳ</th>
                      <th className={`${TH} text-left`}>Hạn dùng</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data.items.map((row) => {
                      const rate = getUsageRate(row);
                      const status = getBatchStatus(row, new Date());
                      return (
                        // Dòng bấm được là tiện lợi cho chuột; đường điều hướng cho bàn phím là nút trong ô "Lô"
                        // (role="button" trên <tr> làm mất ngữ nghĩa dòng của bảng với trình đọc màn hình)
                        <tr
                          key={row.batch_id}
                          onClick={() => setSelected(row)}
                          className="hover:bg-gray-50 cursor-pointer"
                        >
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelected(row);
                              }}
                              aria-label={`Xem mã đã dùng của lô ${row.name}`}
                              className="text-left font-medium text-blue-700 hover:underline"
                            >
                              {row.name}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <TargetTitle item={row} />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">{num(row.total_codes)}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">{num(row.used_codes)}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">{num(row.unused_codes)}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">{num(row.revoked_codes)}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">{num(row.expired_codes)}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <div className="flex items-center gap-2">
                              <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div className="h-full bg-blue-500" style={{ width: `${rate}%` }} />
                              </div>
                              <span>{`${rate}%`}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${status.className}`}
                            >
                              {status.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">{num(row.used_in_period)}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                            {formatYmd(row.expires_at)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <Pagination page={page} perPage={perPage} total={data.total} onPageChange={setPage} />
          </div>
        )}
      </ReportState>

      <UsedCodesDialog batch={selected} filters={filters} onClose={() => setSelected(null)} />
    </div>
  );
};

export default ByBatchTab;
